import React, { ReactNode, useCallback } from "react";
import { logError } from "utils/logger";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { convertLeadToOptions } from "../lib";
import { StartDealRenderProps } from "../types";
import { GET_ACCOUNT_LEAD_OPTIONS, GET_LEAD, CREATE_LEAD_PROPOSAL } from "./index";
import {
  CreateLeadProposalResponse,
  CreateLeadProposalVariables,
  LeadOptionsResponse,
  LeadResponse,
  LeadVariables,
} from "./types";

interface Props {
  children: (startDealRenderProps: StartDealRenderProps) => ReactNode;
}

export const ApiContainer = ({ children }: Props) => {
  const { data, loading: isOptionsLoading } = useQuery<LeadOptionsResponse>(GET_ACCOUNT_LEAD_OPTIONS);

  const [getLead, { loading: isLeadLoading }] = useLazyQuery<LeadResponse, LeadVariables>(GET_LEAD);

  const fetchLead = async (id: string) => {
    try {
      const { data } = await getLead({ variables: { leadId: id } });

      return data?.lead ?? null;
    } catch (error) {
      logError(error);
      return null;
    }
  };

  const [onCreateLeadProposal, { loading: isProposalLoading }] = useMutation<
    CreateLeadProposalResponse,
    CreateLeadProposalVariables
  >(CREATE_LEAD_PROPOSAL);

  const handleCreateLeadProposal = useCallback(
    async (input: CreateLeadProposalVariables["input"]): Promise<CreateLeadProposalResponse["leadProposal"]> => {
      const { data } = await onCreateLeadProposal({ variables: { input } });

      if (data) return data?.leadProposal;

      return {
        success: false,
        id: null,
        leadId: null,
        vendorGUID: null,
        errorMessage: null,
        vendorOpportunityId: null,
      };
    },
    [onCreateLeadProposal]
  );

  return (
    <>
      {children({
        fetchLead,
        isLeadLoading,
        isOptionsLoading,
        isProposalLoading,
        handleCreateLeadProposal,
        options: convertLeadToOptions(data?.leadOptions),
      })}
    </>
  );
};
